import { CheckCircle2, ChevronDown, ChevronUp, ShieldAlert, XCircle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Badge } from './badge';

/**
 * `empty` means WIRED BUT NOTHING CREATED YET — the source is reachable and healthy, it simply has
 * no rows because nobody has built the thing that produces them.
 *
 * It exists because `not_connected` was carrying two very different meanings: "this pipe is broken
 * or unconfigured" and "you have not made a journey yet". Both rendered as a red Not connected, so
 * a healthy new install looked like a broken one and the operator could not tell which they had.
 * A status that cannot distinguish a fault from an empty start teaches people to ignore it.
 */
export type RequirementStatus = 'connected' | 'empty' | 'degraded' | 'not_connected';
export type RequirementKind = 'table' | 'event' | 'identity_source' | 'materializer' | 'field';

export interface Requirement {
  kind: RequirementKind;
  /** For `table | event | identity_source | materializer` this is the table/event
   * name (e.g. `lifecycle.customers`). For `field` this is the mirror-relative
   * field name (e.g. `full_name`) — used for the key but the human-facing label
   * lives in `fieldLabel`. */
  name: string;
  status: RequirementStatus;
  note?: string;
  /** Field-kind extras (only populated when `kind === 'field'`): the mapping
   * domain these fields belong to, a human label, whether it's required, and
   * an optional link to the Data Mapping page to configure it. */
  fieldDomain?: string;
  fieldLabel?: string;
  fieldRequired?: boolean;
  mappingHref?: string;
}

export interface DataRequirementsPanelProps {
  /**
   * List of data requirements for the page. The panel aggregates their
   * statuses into a single header icon — all `connected` renders a
   * subtle green tick; any `degraded` renders a warning; any required
   * source that's `not_connected` escalates to danger.
   */
  requirements: Requirement[];
  /** Human description of refresh cadence, e.g. "hourly" or "nightly 06:00 Asia/Dubai". */
  refreshCadence?: string;
  /** ISO timestamp of the most recent successful refresh. */
  lastUpdatedAt?: string | null;
  /**
   * Optional extra banner content shown above the requirement list,
   * e.g. "Thresholds running on `default-v1` seed values — tune in
   * RFM → Settings."
   */
  degradedBanner?: ReactNode;
  /** Start the panel expanded. Defaults to collapsed. */
  defaultOpen?: boolean;
  className?: string;
}

const KIND_LABEL: Record<RequirementKind, string> = {
  table: 'Table',
  event: 'Event',
  identity_source: 'Identity source',
  materializer: 'Aggregate',
  field: 'Field',
};

const STATUS_COPY: Record<RequirementStatus, string> = {
  connected: 'Connected',
  empty: 'No data yet',
  degraded: 'Degraded',
  not_connected: 'Not connected',
};

/**
 * Page-header panel that tells the user exactly what data the page
 * depends on, whether it's live, and how fresh the most recent refresh
 * was. Module convention — every Lifecycle page ships one.
 *
 * Visual language:
 *   - all `connected`           → tick icon, quiet grey
 *   - any `degraded`            → warning icon, amber
 *   - any required source       → danger icon, red
 *     `not_connected`
 *
 * The header row is always visible; the requirement list collapses
 * by default to stay out of the way once the user's seen the page
 * once.
 */
export function DataRequirementsPanel({
  requirements,
  refreshCadence,
  lastUpdatedAt,
  degradedBanner,
  defaultOpen = false,
  className,
}: DataRequirementsPanelProps) {
  const [open, setOpen] = useState(defaultOpen);

  const worst = worstStatus(requirements);
  const Icon =
    worst === 'connected' ? CheckCircle2 : worst === 'empty' ? CheckCircle2 : worst === 'degraded' ? ShieldAlert : XCircle;
  const iconClass =
    worst === 'connected'
      ? 'text-success'
      : worst === 'empty'
      ? 'text-text-secondary'
      : worst === 'degraded'
      ? 'text-warning'
      : 'text-danger';

  const lastUpdatedLabel = lastUpdatedAt ? formatRelative(lastUpdatedAt) : 'no sync yet';

  return (
    <section
      className={cn(
        'rounded-lg border border-border bg-bg-surface',
        'transition-colors',
        className,
      )}
      aria-labelledby="data-requirements-heading"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="data-requirements-body"
        className={cn(
          'flex w-full items-center justify-between gap-3 px-4 py-3',
          'text-left rounded-lg',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <Icon className={cn('h-4 w-4 shrink-0', iconClass)} aria-hidden />
          <div className="flex min-w-0 flex-col">
            <h2
              id="data-requirements-heading"
              className="text-caption font-medium text-text-primary tracking-wide uppercase"
            >
              Data requirements
            </h2>
            <p className="truncate text-body-sm text-text-secondary">
              {summarizeStatus(requirements)} · last updated {lastUpdatedLabel}
              {refreshCadence ? ` · refreshes ${refreshCadence}` : ''}
            </p>
          </div>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-text-muted shrink-0" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 text-text-muted shrink-0" aria-hidden />
        )}
      </button>

      {open ? (
        <div
          id="data-requirements-body"
          className="flex flex-col gap-3 border-t border-border px-4 py-3"
        >
          {degradedBanner ? (
            <div className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-body-sm text-text-primary">
              {degradedBanner}
            </div>
          ) : null}

          {/* Non-field requirements (table / materializer / event / identity_source). */}
          {requirements.some((r) => r.kind !== 'field') ? (
            <ul className="flex flex-col divide-y divide-border">
              {requirements
                .filter((r) => r.kind !== 'field')
                .map((req, idx) => (
                  <li
                    key={`${req.kind}-${req.name}-${idx}`}
                    className="flex flex-wrap items-start justify-between gap-2 py-2 first:pt-0 last:pb-0"
                  >
                    <div className="flex min-w-0 flex-col">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-caption uppercase tracking-wide text-text-muted">
                          {KIND_LABEL[req.kind]}
                        </span>
                        <span className="font-mono text-body-sm text-text-primary break-all">
                          {req.name}
                        </span>
                      </div>
                      {req.note ? (
                        <p className="mt-1 text-caption text-text-secondary">{req.note}</p>
                      ) : null}
                    </div>
                    <Badge variant={badgeVariantFor(req.status)}>{STATUS_COPY[req.status]}</Badge>
                  </li>
                ))}
            </ul>
          ) : null}

          {/* Field-kind requirements grouped by fieldDomain. */}
          {groupFieldsByDomain(requirements).map((group) => (
            <div
              key={group.domain}
              className="flex flex-col gap-2 rounded-md border border-border bg-bg-inset/50 p-3"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-caption uppercase tracking-wide text-text-muted">
                    eZhire data
                  </span>
                  <span className="font-mono text-body-sm text-text-primary">
                    {group.domain}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-caption">
                  <span
                    className={
                      group.verified === group.total
                        ? 'text-success'
                        : group.hasMissingRequired
                        ? 'text-danger'
                        : 'text-warning'
                    }
                  >
                    {group.verified} / {group.total} verified
                  </span>
                  {group.mappingHref ? (
                    <a
                      href={group.mappingHref}
                      className="text-accent-500 hover:underline"
                    >
                      Configure mapping →
                    </a>
                  ) : null}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
                {group.fields.map((req, idx) => (
                  <div
                    key={`${req.fieldDomain}-${req.name}-${idx}`}
                    className="flex items-center justify-between gap-2 py-0.5"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-body-sm text-text-primary">
                        {req.fieldLabel ?? req.name}
                      </span>
                      {req.fieldRequired ? (
                        <span className="ml-1 text-[11px] text-danger" title="Required">
                          *
                        </span>
                      ) : null}
                      <span className="ml-2 font-mono text-[11px] text-text-muted">
                        {req.name}
                      </span>
                    </div>
                    <FieldStatusDot status={req.status} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function worstStatus(reqs: Requirement[]): RequirementStatus {
  if (reqs.some((r) => r.status === 'not_connected')) return 'not_connected';
  if (reqs.some((r) => r.status === 'degraded')) return 'degraded';
  // `empty` deliberately does NOT escalate the header to a warning: nothing is wrong with a page
  // whose feature has not been used yet, and colouring it amber would train the operator to
  // dismiss the indicator that is supposed to mean something is broken.
  if (reqs.some((r) => r.status === 'empty')) return 'empty';
  return 'connected';
}

function summarizeStatus(reqs: Requirement[]): string {
  const connected = reqs.filter((r) => r.status === 'connected').length;
  const degraded = reqs.filter((r) => r.status === 'degraded').length;
  const empty = reqs.filter((r) => r.status === 'empty').length;
  const notConnected = reqs.filter((r) => r.status === 'not_connected').length;
  const total = reqs.length;

  if (connected === total) return `${total} source${total === 1 ? '' : 's'} connected`;
  const parts: string[] = [];
  if (connected) parts.push(`${connected} connected`);
  if (empty) parts.push(`${empty} with no data yet`);
  if (degraded) parts.push(`${degraded} degraded`);
  if (notConnected) parts.push(`${notConnected} not connected`);
  return parts.join(' · ');
}

function badgeVariantFor(status: RequirementStatus): 'success' | 'warning' | 'danger' | 'default' {
  if (status === 'connected') return 'success';
  if (status === 'degraded') return 'warning';
  // `empty` must NOT fall through to danger. The whole point of the status is to stop a healthy
  // unused feature looking like a broken connection, and a red badge reading "No data yet" would
  // have kept saying exactly what the new status was added to stop saying.
  if (status === 'empty') return 'default';
  return 'danger';
}

interface FieldGroup {
  domain: string;
  fields: Requirement[];
  total: number;
  verified: number;
  hasMissingRequired: boolean;
  mappingHref?: string;
}

function groupFieldsByDomain(reqs: Requirement[]): FieldGroup[] {
  const byDomain = new Map<string, Requirement[]>();
  for (const r of reqs) {
    if (r.kind !== 'field') continue;
    const domain = r.fieldDomain ?? 'unknown';
    const list = byDomain.get(domain) ?? [];
    list.push(r);
    byDomain.set(domain, list);
  }
  return Array.from(byDomain.entries()).map(([domain, fields]) => ({
    domain,
    fields,
    total: fields.length,
    verified: fields.filter((f) => f.status === 'connected').length,
    hasMissingRequired: fields.some(
      (f) => f.fieldRequired && f.status === 'not_connected',
    ),
    mappingHref: fields.find((f) => !!f.mappingHref)?.mappingHref,
  }));
}

function FieldStatusDot({ status }: { status: RequirementStatus }) {
  const dotClass =
    status === 'connected'
      ? 'bg-success'
      : status === 'degraded'
      ? 'bg-warning'
      : 'bg-danger';
  const label =
    status === 'connected' ? 'Verified' : status === 'degraded' ? 'Mapped' : 'Missing';
  return (
    <span className="flex items-center gap-1.5 text-caption text-text-muted">
      <span className={cn('h-2 w-2 rounded-full', dotClass)} aria-hidden />
      <span>{label}</span>
    </span>
  );
}

/**
 * Format a timestamp as "8 min ago" / "3 h ago" / "2 d ago". Kept
 * inline to avoid pulling a whole date lib into packages/ui.
 */
function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const diffSec = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} d ago`;
}
